import { TRASH_DIR, type FileEntry, type SyncFs } from './reconcile';

/**
 * The sync's file system, on a GitHub repository.
 *
 * The point of this file is that there is nothing else to it: the whole
 * two-way sync — pairings, conflicts, renames, the trash — already runs
 * against the small `SyncFs` interface in reconcile.ts, so syncing with a repo
 * is one adapter rather than a second sync engine. What the repo holds is
 * ordinary `.md` files, readable by anything, and the history is the repo's.
 *
 * Two details are worth knowing:
 *
 *  - **Modification times.** Git has none, and asking for the last commit of
 *    every file would be one request per note. The adapter uses the blob's
 *    sha instead, folded into the number the interface expects: it changes
 *    exactly when the file changes, which is all the reconciler asks of it.
 *  - **The token** is a GitHub personal access token with contents read and
 *    write on the one repository. It stays on the device (see storage.ts) and
 *    is sent to api.github.com and nowhere else.
 */

const API = 'https://api.github.com';

export interface GithubRepo {
  /** `owner/name`. */
  repo: string;
  branch: string;
  token: string;
  /** Subfolder inside the repo, or '' for the whole thing. */
  path?: string;
}

export class GithubError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'GithubError';
  }
}

/**
 * A blob sha as the number `FileEntry.lastModified` wants.
 *
 * It is not a time and is never shown as one; it is compared with the value
 * stored at the last sync to tell an untouched file from a changed one.
 */
export function shaStamp(sha: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < sha.length; i++) {
    h ^= sha.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function encodeContent(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

interface TreeResponse {
  tree?: { path: string; type: string; sha: string }[];
  truncated?: boolean;
}

interface ContentResponse {
  content?: { sha?: string };
}

/** What a caller needs to do HTTP; injectable so the rules can be tested without a network. */
export type Fetcher = typeof fetch;

export function githubFs(config: GithubRepo, doFetch: Fetcher = fetch): SyncFs {
  const prefix = config.path ? `${config.path.replace(/^\/+|\/+$/g, '')}/` : '';
  /** Blob shas from the last listing, needed to update or delete a file. */
  const shas = new Map<string, string>();

  const request = async (url: string, init: RequestInit = {}): Promise<Response> => {
    const response = await doFetch(`${API}${url}`, {
      ...init,
      headers: {
        accept: 'application/vnd.github+json',
        authorization: `Bearer ${config.token}`,
        'x-github-api-version': '2022-11-28',
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    });
    if (!response.ok) {
      throw new GithubError(`GitHub replied ${response.status}`, response.status);
    }
    return response;
  };

  const full = (path: string) => `${prefix}${path}`;

  /** The sha a write needs in order to replace a file rather than collide with it. */
  const shaOf = async (path: string): Promise<string | undefined> => {
    const known = shas.get(path);
    if (known) return known;
    try {
      const response = await request(
        `/repos/${config.repo}/contents/${encodePath(full(path))}?ref=${encodeURIComponent(config.branch)}`,
      );
      const body = (await response.json()) as { sha?: string };
      if (body.sha) shas.set(path, body.sha);
      return body.sha;
    } catch (error) {
      // Not there yet: this is a new file, which is not a failure.
      if (error instanceof GithubError && error.status === 404) return undefined;
      throw error;
    }
  };

  const write = async (path: string, text: string): Promise<number> => {
    const sha = await shaOf(path);
    const response = await request(`/repos/${config.repo}/contents/${encodePath(full(path))}`, {
      method: 'PUT',
      body: JSON.stringify({
        message: `markbird: ${sha ? 'update' : 'add'} ${path}`,
        content: encodeContent(text),
        branch: config.branch,
        ...(sha ? { sha } : {}),
      }),
    });
    const body = (await response.json()) as ContentResponse;
    const next = body.content?.sha ?? '';
    if (next) shas.set(path, next);
    else shas.delete(path);
    return shaStamp(next);
  };

  const remove = async (path: string): Promise<void> => {
    const sha = await shaOf(path);
    if (!sha) return;
    await request(`/repos/${config.repo}/contents/${encodePath(full(path))}`, {
      method: 'DELETE',
      body: JSON.stringify({ message: `markbird: remove ${path}`, sha, branch: config.branch }),
    });
    shas.delete(path);
  };

  return {
    async list() {
      const response = await request(
        `/repos/${config.repo}/git/trees/${encodeURIComponent(config.branch)}?recursive=1`,
      );
      const body = (await response.json()) as TreeResponse;
      if (body.truncated) {
        throw new GithubError('The repository is too large to list in one request.', 422);
      }
      const out: FileEntry[] = [];
      shas.clear();
      for (const item of body.tree ?? []) {
        if (item.type !== 'blob' || !/\.md$/i.test(item.path)) continue;
        if (prefix && !item.path.startsWith(prefix)) continue;
        const path = item.path.slice(prefix.length);
        // Hidden folders (.github, .obsidian, our own trash) are not notes.
        if (path.split('/').some((part) => part.startsWith('.'))) continue;
        shas.set(path, item.sha);
        out.push({ path, lastModified: shaStamp(item.sha) });
      }
      return out;
    },

    async read(path) {
      const response = await request(
        `/repos/${config.repo}/contents/${encodePath(full(path))}?ref=${encodeURIComponent(config.branch)}`,
        { headers: { accept: 'application/vnd.github.raw+json' } },
      );
      return response.text();
    },

    write,

    async trash(path) {
      // Written first, removed second: if anything fails in between the note
      // exists twice in the repo, not zero times.
      const text = await this.read(path);
      await write(`${TRASH_DIR}/${timestamp()}/${path}`, text);
      await remove(path);
    },

    remove,
  };
}

function timestamp(date = new Date()): string {
  return date.toISOString().replace(/[:.]/g, '-');
}

/** Percent-encode a path for the contents API, keeping the slashes. */
function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

/** Check a repository and token before anything is synced with them. */
export async function checkGithubAccess(
  config: GithubRepo,
  doFetch: Fetcher = fetch,
): Promise<{ ok: true } | { ok: false; reason: 'auth' | 'repo' | 'branch' | 'error' }> {
  try {
    const response = await doFetch(
      `${API}/repos/${config.repo}/branches/${encodeURIComponent(config.branch)}`,
      {
        headers: {
          accept: 'application/vnd.github+json',
          authorization: `Bearer ${config.token}`,
          'x-github-api-version': '2022-11-28',
        },
      },
    );
    if (response.ok) return { ok: true };
    if (response.status === 401 || response.status === 403) return { ok: false, reason: 'auth' };
    if (response.status === 404) {
      // A missing repository and a missing branch are the same status; the
      // difference is what the person has to fix, so it is worth one request.
      const repo = await doFetch(`${API}/repos/${config.repo}`, {
        headers: { authorization: `Bearer ${config.token}` },
      });
      return { ok: false, reason: repo.ok ? 'branch' : 'repo' };
    }
    return { ok: false, reason: 'error' };
  } catch {
    return { ok: false, reason: 'error' };
  }
}
